import React, { useState, useEffect } from "react";
import { Accordion } from "react-bootstrap";
import { Link, useLocation, useNavigate } from "react-router-dom";
import "./sidebar.css";
import LogoutIcon from "../../../images/Logout.svg";
import SidebarLogo from "../../../images/sidebar_logo.svg";
import { getMenuItems } from "./Sidemenus";
import { DropdownSvg } from "../../../svgFiles/DropdownSvg";
import { jwtDecode } from "../../../helpers/AccessControlUtils";
import useTranslator from "../../../hooks/useTranslator";
import { getCurrentLanguage } from "../../../helpers/Utils";
import { filterMenuByPermissions } from "../../../helpers/permissionHelper";
import { permissionConfig } from "../../../helpers/permissionConfig";
import usePermissions from "../../../hooks/usePermissions";
import { useDispatch } from "react-redux";
import { clearPermissions } from "../../../app/globalSlice";
import LogoutConfirmationModal from "../../../features/profile/LogoutConfirmationModal";

const Sidebar = () => {
  const { _t } = useTranslator();
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [activeKey, setActiveKey] = useState(null);
  const [userRole, setUserRole] = useState(null);
  const [filteredMenu, setFilteredMenu] = useState([]);
  const [showLogoutModal, setShowLogoutModal] = useState(false);
  const [isLoggingOut, setIsLoggingOut] = useState(false);

  // Use the custom hook to automatically fetch permissions
  const { permissions, isLoading } = usePermissions();

  useEffect(() => {
    const decodedToken = jwtDecode();
    if (decodedToken) {
      setUserRole(decodedToken.role);
    }
  }, []);

  // Filter menu when permissions change
  useEffect(() => {
    if (permissions && permissions.length > 0) {
      const filtered = filterMenuByPermissions(permissionConfig, permissions);
      setFilteredMenu(filtered);
    } else if (!isLoading && !localStorage.getItem('authToken')) {
      // No token = not logged in, show nothing
      setFilteredMenu([]);
    }
    // While loading (isLoading = true), keep filteredMenu empty — show nothing
  }, [permissions, isLoading]);

  useEffect(() => {
    // Find the active parent menu index
    const activeParentIndex = filteredMenu.findIndex((item) =>
      item.subMenu?.some((subItem) => location.pathname === subItem.path)
    );
    // Set activeKey only if a submenu item is selected
    setActiveKey(activeParentIndex !== -1 ? String(activeParentIndex) : null);
  }, [location.pathname, filteredMenu]);

  const handleLogout = () => {
    setShowLogoutModal(true);
  };

  const handleConfirmLogout = () => {
    let locale = getCurrentLanguage();
    setIsLoggingOut(true); // prevent skeleton flash during logout    
    dispatch(clearPermissions());
    localStorage.clear();

    if (locale) {
      localStorage.setItem("i18nextLng", locale);
    }
    setTimeout(() => {
      navigate("/login", { replace: true });      
    }, 1000);    
  };

  const handleLogoClick = () => {
    navigate("/dashboard");
  };

  const renderMenuItem = (item, index) => {
    const isParentActive = item.subMenu?.some(
      (subItem) => location.pathname === subItem.path
    );
    const isActive = location.pathname === item.path || isParentActive;

    return (
      <li key={index} className={`outerlist ${isActive ? "activeLink" : ""}`}>
        {item.subMenu?.length > 0 ? (
          <Accordion.Item eventKey={String(index)} className="border-0">
            <Accordion.Header>
              {item.icon} {_t(item.title, item.title)}{" "}
              <span className="arrow_down">{DropdownSvg}</span>
            </Accordion.Header>
            <Accordion.Body>
              <ul className="list-unstyled">
                {item.subMenu.map((subItem, subIndex) => (
                  <li
                    key={subIndex}
                    className={
                      location.pathname === subItem.path
                        ? "active_accordian"
                        : ""
                    }
                  >
                    <Link to={subItem.path} className="text-decoration-none">
                      {_t(subItem.title, subItem.title)}
                    </Link>
                  </li>
                ))}
              </ul>
            </Accordion.Body>
          </Accordion.Item>
        ) : (
          <Link
            to={item.path}
            className="text-decoration-none d-flex align-items-center gap-2"
          >
            {item.icon} {_t(item.title, item.title)}
          </Link>
        )}
      </li>
    );
  };

  const renderSkeleton = () => (
    <div className="sidebar-skeleton">
      {[...Array(8)].map((_, i) => (
        <div key={i} className="skeleton-item">
          <div className="skeleton-icon" />
          <div className="skeleton-text" style={{ width: `${50 + (i % 3) * 20}%` }} />
        </div>
      ))}
    </div>
  );

  return (
    <div className="side_bar">
      <LogoutConfirmationModal show={showLogoutModal} setShow={setShowLogoutModal} onConfirmLogout={handleConfirmLogout} />
      <div className="side_logo text-center">
        <img src={SidebarLogo} alt="logo" onClick={handleLogoClick} />
      </div>

      <div className="menu_items">
        {isLoading && !isLoggingOut ? renderSkeleton() : (
          <ul className="list-unstyled">
            <Accordion
              activeKey={activeKey}
              onSelect={(key) => setActiveKey(key)}
            >
              {filteredMenu.map(renderMenuItem)}
            </Accordion>
          </ul>
        )}
      </div>

      <div className="logout_container">
        <li className="logout" onClick={handleLogout}>
          <span>
            <img src={LogoutIcon} alt="logout" /> {_t("log_out", "Logout")}
          </span>
        </li>
      </div>
    </div>
  );
};

export default Sidebar;